import { IPromise } from "angular";
import { DayOfWeek, Instant, LocalDate } from "@js-joda/core";
import { Rawify } from "../@ts-utils/rawify";
import { AssertNever } from "../consts/assertNever.const";
import {
  PatientId,
  PlanOfCareDutyId,
  PlanOfCareId,
  PlanOfCareItemId,
  ServiceCodeId,
} from "../messages/ids";
import {
  AgencyMemberCreatePlanOfCareRequest,
  GeneratePatientDutySheetsCSVResponse,
  PatientPlanOfCare,
  PatientPlansOfCareDuty,
  PatientPlansOfCareResponse,
  PlanOfCareDutyPeriod,
} from "../messages/plan_of_care";
import {
  AgencyMemberCreateCustomPlanOfCareTypeForOfficesRequest,
  DefaultPOCTaskItems,
} from "../messages/plan_of_care_type";
import { formatPlanOfCareDutyTimesPerDay } from "../../../../webapp-react/src/shared/utils/plan-of-care-duty-utils";
import { Api } from "./Api";
import { DatabaseApiService } from "./db";
import { Endpoint } from "./endpoint.service";

//! @ngInject
export interface PlanOfCareDutyTableRow {
  name: string;
  code: string;
  section: string;
  /** Unique base service codes without modifiers; "All" when the item is not scoped. */
  serviceCodes: string;
  /**
   * How often the duty is performed, as text: "2x" for a duty due the same number
   * of times on each of its days, "MONx2, SUNx1" where the days differ, and empty
   * for the ordinary once-a-day duty.
   */
  timesPerDay: string;
  /** Whether only caregivers validated for the duty see it on their visits. */
  validatedOnly: boolean;
  mo: boolean;
  tu: boolean;
  we: boolean;
  th: boolean;
  fr: boolean;
  sa: boolean;
  su: boolean;
  frequency: string;
  duration: string;
  notes: string;
}

export interface GeneratePatientDutySheetRow {
  id: PlanOfCareDutyId;
  period: PlanOfCareDutyPeriod;
  daysOfWeek: DayOfWeek[] | null;
  visitsPerWeek: number | null;
  frequency: number | null;
  notes: string | null;
  startDate: LocalDate;
  caregiverName: string;
  isCompleted: boolean;
  item: {
    id: PlanOfCareItemId;
    label: string;
    code: string;
  };
}

export class PlanOfCareService {
  constructor(
    private $rootScope: ng.IRootScopeService,
    private endpoint: Endpoint,
    private api: Api,
    private assertNever: AssertNever,
    private DatabaseApi: DatabaseApiService
  ) {}

  /**
   * Create a new plan of care for a patient
   */
  async create(params: AgencyMemberCreatePlanOfCareRequest & { patientId: PatientId }) {
    const url = this.endpoint({
      path: "agencies/:agencyId/agency_members/:agencyMemberId/patients/:patientId/plans_of_care",
      params: {
        agencyId: this.$rootScope.agencyId,
        agencyMemberId: this.$rootScope.agencyMemberId,
        patientId: params.patientId,
      },
    });

    return this.api.post(url, params).then(() => void null);
  }

  /**
   * Get all plans of care for a patient
   */
  async getByPatientId(patientId: PatientId): Promise<PatientPlanOfCare[]> {
    const url = this.endpoint({
      path: "agencies/:agencyId/agency_members/:agencyMemberId/patients/:patientId/plans_of_care",
      params: {
        agencyId: this.$rootScope.agencyId,
        agencyMemberId: this.$rootScope.agencyMemberId,
        patientId: patientId,
      },
    });

    return this.api.get<Rawify<PatientPlansOfCareResponse>>(url).then(({ data }) =>
      data.plansOfCare.map((planOfCare) => ({
        ...planOfCare,
        duties: planOfCare.duties.map((duty) => ({
          ...duty,
          daysOfWeek: duty.daysOfWeek?.map((dayOfWeek) => DayOfWeek.valueOf(dayOfWeek)) ?? null,
        })),
        startDate: planOfCare.startDate ? LocalDate.parse(planOfCare.startDate) : null,
        endDate: planOfCare.endDate ? LocalDate.parse(planOfCare.endDate) : null,
        submittedAt: Instant.parse(planOfCare.submittedAt),
      }))
    );
  }

  /**
   * Get a plan of care pdf url
   */
  async getPdfUrl(planOfCareId: PlanOfCareId) {
    const url = this.endpoint({
      path: "agencies/:agencyId/agency_members/:agencyMemberId/plans_of_care/:planOfCareId/generate_url",
      params: {
        agencyId: this.$rootScope.agencyId,
        agencyMemberId: this.$rootScope.agencyMemberId,
        planOfCareId: planOfCareId,
      },
    });

    return this.api.get<{ fileUrl: string | null }>(url).then(({ data }) => data.fileUrl);
  }

  /**
   * Regenerate a plan of care pdf
   */
  async regeneratePdf(planOfCareId: PlanOfCareId): Promise<string | null> {
    const url = this.endpoint({
      path: "agencies/:agencyId/agency_members/:agencyMemberId/plans_of_care/:planOfCareId/regenerate_pdf",
      params: {
        agencyId: this.$rootScope.agencyId,
        agencyMemberId: this.$rootScope.agencyMemberId,
        planOfCareId: planOfCareId,
      },
    });

    return this.api.post<{ fileUrl: string | null }>(url).then(({ data }) => data.fileUrl);
  }

  /**
   * Get default hard-coded plan of care task items
   */
  getDefaultPOCTaskItems(): IPromise<DefaultPOCTaskItems> {
    const url = this.endpoint({
      path: "agencies/:agencyId/agency_members/:agencyMemberId/default_poc_task_items",
      params: {
        agencyId: this.$rootScope.agencyId,
        agencyMemberId: this.$rootScope.agencyMemberId,
      },
    });

    return this.api.get<DefaultPOCTaskItems>(url).then((res) => res.data);
  }

  async generateDutySheetsCsv(params: {
    patientId: PatientId;
    planOfCareId: PlanOfCareId;
    from: LocalDate;
    to: LocalDate;
  }): Promise<GeneratePatientDutySheetRow[]> {
    const url = this.endpoint({
      path: "agencies/:agencyId/agency_members/:agencyMemberId/plans_of_care/:planOfCareId/generate_duty_sheets_csv_by_dates",
      params: {
        agencyId: this.$rootScope.agencyId,
        agencyMemberId: this.$rootScope.agencyMemberId,
        planOfCareId: params.planOfCareId,
      },
      queries: {
        from: params.from.toString(),
        to: params.to.toString(),
      },
    });

    return this.api.get<Rawify<GeneratePatientDutySheetsCSVResponse>>(url).then(({ data }) => {
      return data.duties.map(
        (duty): GeneratePatientDutySheetRow => ({
          id: duty.id,
          period: duty.period,
          daysOfWeek: duty.daysOfWeek?.map((day) => DayOfWeek.valueOf(day)) ?? null,
          item: duty.item,
          caregiverName: duty.caregiverName,
          isCompleted: duty.isCompleted,
          startDate: LocalDate.parse(duty.startDate),
          visitsPerWeek: duty.visitsPerWeek,
          frequency: duty.frequency,
          notes: duty.notes,
        })
      );
    });
  }

  /**
   * Create a new custom plan of care type for an office/s
   */
  createCustomPlanOfCareType(
    params: AgencyMemberCreateCustomPlanOfCareTypeForOfficesRequest
  ): IPromise<void> {
    const url = this.endpoint({
      path: "agencies/:agencyId/agency_members/:agencyMemberId/custom_plan_of_care_type",
      params: {
        agencyId: this.$rootScope.agencyId,
        agencyMemberId: this.$rootScope.agencyMemberId,
      },
    });

    return this.api.post(url, params).then(() => void null);
  }

  async generateDutySheetPdf(params: {
    patientId: PatientId;
    planOfCareId: PlanOfCareId;
    from: LocalDate;
    to: LocalDate;
  }) {
    return this.api
      .get<{ fileUrl: string }>(
        this.endpoint({
          path: "agencies/:agencyId/patients/:patientId/plans_of_care/:planOfCareId/generate_duty_sheets_pdf_by_dates",
          params: {
            agencyId: this.$rootScope.agencyId,
            patientId: params.patientId,
            planOfCareId: params.planOfCareId,
          },
          queries: {
            from: params.from.toString(),
            to: params.to.toString(),
          },
        })
      )
      .then(({ data }) => data.fileUrl);
  }

  // This method should probably not be here, but it's not clear where it should go
  mapToTableRow(duty: PatientPlansOfCareDuty): PlanOfCareDutyTableRow {
    return {
      name: duty.item.label,
      code: duty.item.code,
      section: duty.item.sectionName,
      serviceCodes: this.formatServiceCodes(duty.item.serviceCodeIds ?? []),
      timesPerDay: formatPlanOfCareDutyTimesPerDay(duty.timesPerDay, duty.timesPerDayByDay),
      validatedOnly: duty.caregiverRestricted === true,
      frequency: (() => {
        switch (duty.period) {
          case "AS_REQUESTED":
            return "As requested";
          case "EVERY_VISIT":
            return "Every visit";
          case "DAYS_A_WEEK":
            if (duty.frequency === null) {
              return "";
            }
            // A weekly count with no days named, as a past service plan states many of its duties.
            return duty.visitsPerWeek === null
              ? `${duty.frequency}x week`
              : `${duty.frequency}/${duty.visitsPerWeek} days a week`;
          case "DAILY":
            return "Daily";
          case "WEEKLY":
            return "Weekly";
          case "1_2X_WEEKLY":
            return "1-2x Weekly";
          case "DAILY_AS_NEEDED":
            return "Daily (as needed)";
          case "WEEKLY_AS_NEEDED":
            return "Weekly (as needed)";
          case "1_2X_WEEKLY_AS_NEEDED":
            return "1-2x Weekly (as needed)";
          default:
            this.assertNever(duty.period);
        }
      })(),
      // Minutes as sent, not an hours/minutes split: the figure is a
      // per-occurrence task length, which is nearly always under an hour.
      duration: duty.durationMinutes !== null ? `${duty.durationMinutes} min` : "",
      notes: duty.notes ?? "",
      mo: this.isDutyRequiredInDay({ duty, day: DayOfWeek.MONDAY }),
      tu: this.isDutyRequiredInDay({ duty, day: DayOfWeek.TUESDAY }),
      we: this.isDutyRequiredInDay({ duty, day: DayOfWeek.WEDNESDAY }),
      th: this.isDutyRequiredInDay({ duty, day: DayOfWeek.THURSDAY }),
      fr: this.isDutyRequiredInDay({ duty, day: DayOfWeek.FRIDAY }),
      sa: this.isDutyRequiredInDay({ duty, day: DayOfWeek.SATURDAY }),
      su: this.isDutyRequiredInDay({ duty, day: DayOfWeek.SUNDAY }),
    };
  }

  isDutyRequiredInDay = (params: { duty: PatientPlansOfCareDuty; day: DayOfWeek }) => {
    return params.duty.daysOfWeek?.includes(params.day) === true;
  };

  /**
   * Base export codes for an item's service-code scope, with modifier variants
   * displayed once. An unscoped item applies to every service, which is what "All"
   * says; an id the cache does not know (a code deactivated since) is shown by
   * number rather than dropped, so the row never claims a narrower scope than
   * the server applies.
   */
  private formatServiceCodes(serviceCodeIds: ServiceCodeId[]): string {
    if (serviceCodeIds.length === 0) {
      return "All";
    }
    const serviceCodes = this.DatabaseApi.serviceCodes();
    const codes = serviceCodeIds.map((id) => {
      const serviceCode = serviceCodes.find((serviceCode) => serviceCode.id === id);
      if (serviceCode === undefined) {
        return `#${id}`;
      }
      const baseCode = serviceCode.exportCode.split(":")[0].replace(/\s/g, "").toUpperCase();
      return baseCode || serviceCode.code;
    });
    return [...new Set(codes)].join(", ");
  }
}
